import React, { useEffect } from "react";
import {
  Avatar,
  Box,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import "../../styles/common.css";
import { capitalizeFirstLetter, validateDate } from "../../utils/utils";
import CalendarIcon from "../../assets/contacts/calendaricon.svg";
import CompanyIcon from "../../assets/contacts/companyicon.svg";
import JobIcon from "../../assets/contacts/jobIcon.svg";
import LinkedIn from "../../assets/contacts/linkedinicon.svg";
import Mailicon from "../../assets/contacts/mailIcon.svg";
import CommonService from "../../services/common/commonService";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";

interface ContactsProfileProps {
  email: string;
  profileOrgId: string;
  isSlideOverOpen?: boolean;
}

const ContactsProfile: React.FC<ContactsProfileProps> = ({
  email,
  profileOrgId,
  isSlideOverOpen,
}) => {
  
  const { showSnackbar } = useDisplaySnackbar();
  const {
    isLoading: isUserLoading,
    isFetching: isUserFetching,
    isSuccess: isUserSuccess,
    isError: isUserError,
    error: userError,
    data: userData,
  } = CommonService.useGetUserProfile(
    {
      profileOrgId: profileOrgId,
      employeeEmail: email,
    },
    !!isSlideOverOpen && !!email && !!profileOrgId
  )

  useEffect(() => {
    if (isUserError){
      showSnackbar("error", userError?.message || "Failed to load data.");
    }
  },[isUserFetching, isUserLoading, isUserError, userError, isUserSuccess])
  return (
    <Box className="contacts-card">
      <Box className="contacts-box">
        <Box className="contacts-name">
          {userData?.pictureUrl ? (
            <Avatar
              src={userData.pictureUrl}
              alt={userData.fullName}
              sx={{ width: 68, height: 68 }}
            />
          ) : (
            <Avatar sx={{ width: 68, height: 68 }}>
              {userData?.fullName?.charAt(0).toUpperCase()}
            </Avatar>
          )}
          <Box display="flex" flexDirection="column" gap={0.5}>
            <Typography variant="h6" fontWeight={600}>
              {userData?.fullName}
            </Typography>
            <Box className="email-linked-box">
              <Tooltip title={email} arrow placement="bottom" 
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: "#6036D6",   // background color
                      color: "#fff",        // text color
                      fontSize: "12px",
                      borderRadius: "6px",
                    },
                  },
                  arrow: {
                    sx: {
                      color: "#6036D6", // arrow matches tooltip background
                    },
                  },
                }}>
                <IconButton size="small" 
                  sx={{
                    borderRadius: "6px",
                    p: 0.5,
                    "&:hover": {
                      backgroundColor: "rgba(96, 54, 214, 0.1)",
                    },
                  }}>
                  <img src={Mailicon} alt="Email" width={16} height={16} />
                </IconButton>
              </Tooltip>

              <Tooltip title={userData?.linkedInProfile || "No Linkedin Profile"} arrow placement="bottom"
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: "#6036D6",   // background color
                      color: "#fff",        // text color
                      fontSize: "12px",
                      borderRadius: "6px",
                    },
                  },
                  arrow: {
                    sx: {
                      color: "#6036D6", // arrow matches tooltip background
                    },
                  },
                }}
              >
                <IconButton size="small"
                  sx={{
                    borderRadius: "6px",
                    p: 0.5,
                    "&:hover": {
                      backgroundColor: "rgba(96, 54, 214, 0.1)",
                    },
                  }}>
                  <img src={LinkedIn} alt="LinkedIn" width={16} height={16} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        <Box className="contacts-three-box">
          {userData?.jobTitle && (
            <>
            <Box className="info-box">
              <Box className="info-label">
                <img src={JobIcon} alt="Job" width={20} height={20} />
                <Typography>Job Title</Typography>
              </Box>
              <Typography className="info-value">{userData?.jobTitle}</Typography>
            </Box>
            <Box className="vertical-divider" />
            </>
          )}

          <Box className="info-box">
            <Box className="info-label">
              <img src={CompanyIcon} alt="Company" width={20} height={20} />
              <Typography>Company</Typography>
            </Box>
            <Typography className="info-value">
              {capitalizeFirstLetter(userData?.orgName)}
            </Typography>
          </Box>

          <Box className="vertical-divider" />

          <Box className="info-box">
            <Box className="info-label">
              <img src={CalendarIcon} alt="Interaction" width={20} height={20} />
              <Typography>Upcoming Interaction</Typography>
            </Box>
            <Typography className="info-value">
              {userData?.upcomingInteraction
                ? validateDate(userData.upcomingInteraction)
                : "No upcoming interaction"}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ContactsProfile;
